/* eslint-disable no-use-before-define, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, differenceInMilliseconds } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '../../lib/api';
import { NATURE_ROMANTIQUE_ASSETS } from '../../lib/defaultThemeAssets';
import './nature.css';
import TemplateAlexandreSophieRSVP from '../../components/TemplateAlexandreSophieRSVP';
import NatureShader from './NatureShader';

export default function NatureRomantique({ wedding, heroImage, accentImage, isSectionVisible }) {
    const [navbarScrolled, setNavbarScrolled] = useState(false);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
    const [isRSVPOpen, setIsRSVPOpen] = useState(false);
    const natureGalleryImages = NATURE_ROMANTIQUE_ASSETS.gallery.map(
        (fallback, index) => wedding?.nature_gallery_images?.[index] || fallback
    );

    // Guestbook state
    const [newMessage, setNewMessage] = useState({ guest_name: "", content: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messages, setMessages] = useState([]);

    // FAQ state
    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    const fetchMessages = useCallback(async () => {
        try {
            const res = await api.get(`/public/wedding/${wedding.slug}/messages`);
            if (res.data) setMessages(res.data);
        } catch (error) {
            console.error("Erreur lors de la récupération des messages:", error);
        }
    }, [wedding?.slug]);

    const showSection = useCallback((sectionId) => {
        if (!isSectionVisible) return true;
        return isSectionVisible(sectionId);
    }, [isSectionVisible]);

    // Dress Code state
    const [copiedColor, setCopiedColor] = useState(null);
    const handleCopy = (color) => {
        navigator.clipboard.writeText(color);
        setCopiedColor(color);
        setTimeout(() => setCopiedColor(null), 1500);
    };
    const toggleFaq = (index) => setOpenFaqIndex(openFaqIndex === index ? null : index);

    useEffect(() => {
        if (showSection("guestbook") && wedding?.slug) {
            fetchMessages();
        }
    }, [wedding?.slug, showSection, fetchMessages]);

    // Gifts state
    const [gifts, setGifts] = useState([]);
    const [claimingGift, setClaimingGift] = useState(null);
    const [guestName, setGuestName] = useState("");
    const [contributionAmount, setContributionAmount] = useState("");
    const [isClaiming, setIsClaiming] = useState(false);

    useEffect(() => {
        if (wedding?.slug && wedding?.show_gifts !== false) {
            api.get(`/public/wedding/${wedding.slug}/gifts`).then((r) => setGifts(r.data)).catch(() => {});
        }
    }, [wedding?.slug, wedding?.show_gifts]);

    const handleClaimGift = async (e) => {
        e.preventDefault();
        if (!guestName || !claimingGift) return;
        setIsClaiming(true);
        try {
            if (claimingGift.type === "cash") {
                const amount = Number(contributionAmount);
                if (!Number.isFinite(amount) || amount <= 0) {
                    toast.error("Veuillez indiquer un montant valide.");
                    return;
                }
                const response = await api.post("/stripe/checkout", {
                    gift_id: claimingGift.id,
                    amount,
                    guest_name: guestName,
                    rsvp_token: "",
                });
                if (response.data?.url) {
                    window.location.assign(response.data.url);
                    return;
                }
                throw new Error("Lien de contribution indisponible");
            }

            await api.post(`/public/wedding/${wedding.slug}/gifts/${claimingGift.id}/claim`, { guest_name: guestName });
            const r = await api.get(`/public/wedding/${wedding.slug}/gifts`);
            setGifts(r.data);
            setClaimingGift(null);
            setGuestName("");
            toast.success("Cadeau réservé avec succès !");
        } catch (error) {
            toast.error("Une erreur est survenue lors de la réservation.");
        } finally {
            setIsClaiming(false);
        }
    };


    const handleMessageSubmit = async (e) => {
        e.preventDefault();
        if (!newMessage.guest_name || !newMessage.content) {
            toast.error("Veuillez remplir tous les champs");
            return;
        }
        setIsSubmitting(true);
        try {
            await api.post(`/public/wedding/${wedding.slug}/messages`, newMessage);
            toast.success("Message envoyé !");
            setNewMessage({ guest_name: "", content: "" });
            fetchMessages();
        } catch (error) {
            console.error("Erreur d'envoi:", error);
            toast.error("Erreur lors de l'envoi du message");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setNavbarScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [fetchMessages, showSection]);

    useEffect(() => {
        if (!wedding?.date) return;
        const targetDate = parseISO(wedding.date);
        
        const updateCountdown = () => {
            const now = new Date();
            const diff = differenceInMilliseconds(targetDate, now);
            if (diff > 0) {
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
                const minutes = Math.floor((diff / 1000 / 60) % 60);
                setTimeLeft({ days, hours, minutes });
            }
        };
        
        updateCountdown();
        const interval = setInterval(updateCountdown, 60000);
        return () => clearInterval(interval);
    }, [wedding?.date]);


    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const bride = wedding?.partner1 || wedding?.bride_name || "Amélie";
    const groom = wedding?.partner2 || wedding?.groom_name || "Alexandre";
    const initials = `${bride.charAt(0)}&${groom.charAt(0)}`;
    
    let formattedDate = "15 Juin 2025";
    if (wedding?.date) {
        try {
            formattedDate = format(parseISO(wedding.date), 'dd MMMM yyyy', { locale: fr });
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <div className="bg-surface text-anthracite font-body-md overflow-x-hidden antialiased relative nature-theme">
            {/* Shader Background */}
            <NatureShader />
            
            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 backdrop-blur-md border-b border-rose-gold/20 ${navbarScrolled ? 'bg-off-white/95 dark:bg-anthracite/95 shadow-sm' : 'bg-off-white/80 dark:bg-anthracite/80'}`}>
                <div className="flex justify-between items-center px-margin-mobile md:px-margin-desktop py-6 max-w-container-max mx-auto">
                    <button type="button"  className="font-headline-md text-headline-md text-anthracite dark:text-off-white tracking-widest scale-95 duration-200 ease-in-out" onClick={(e) => {e.preventDefault(); window.scrollTo(0,0);}}>{initials}</button>
                    <div className="hidden md:flex gap-6 items-center">
                        {[
                            { id: "story", href: "#histoire", label: "Histoire" },
                            { id: "invitation", href: "#invitation", label: "Faire-part" },
                            { id: "programme", href: "#programme", label: "Programme" },
                            { id: "gallery", href: "#galerie", label: "Galerie" },
                            { id: "dress_code", href: "#dresscode", label: "Dress Code" },
                            { id: "infos", href: "#lieu", label: "Le Lieu" },
                            { id: "lodgings", href: "#hebergements", label: "Hébergements" },
                            { id: "faq", href: "#faq", label: "FAQ" },
                            { id: "guestbook", href: "#livredor", label: "Livre d'or" },
                            { id: "gifts", href: "#cadeaux", label: "Cadeaux" }
                        ].map(link => {
                            // Hide dress code completely if no data exists at all and it's not active
                            if (link.id === "dress_code" && !showSection("dress_code") && !wedding?.dress_code && !(wedding?.dress_code_colors?.length > 0)) return null;
                            // Hide gifts completely if explicitly disabled via its own toggle
                            if (link.id === "gifts" && (wedding?.show_gifts === false || !gifts || gifts.length === 0)) return null;
                            
                            return (
                                <a 
                                    key={link.id}
                                    onClick={(e) => { 
                                        if (!showSection(link.id)) { 
                                            e.preventDefault(); 
                                            toast.info("Bientôt disponible ⏳"); 
                                        } 
                                    }} 
                                    className="font-label-caps text-label-caps text-anthracite dark:text-off-white hover:text-terracotta transition-all duration-300 cursor-pointer" 
                                    href={link.href}
                                >
                                    {link.label}
                                </a>
                            );
                        })}
                    </div>
                    <button onClick={() => setIsRSVPOpen(true)} className="shimmer-btn font-label-caps text-label-caps bg-anthracite text-off-white px-6 py-2 rounded duration-200 ease-in-out hover:bg-terracotta transition-colors">RSVP</button>
                </div>
            </nav>

            {/* 1. Hero Section */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img alt="Hero background" className="w-full h-full object-cover ken-burns img-desat" src={heroImage || NATURE_ROMANTIQUE_ASSETS.hero} />
                    <div className="absolute inset-0 bg-surface/20 backdrop-blur-[2px]"></div>
                </div>
                <motion.div 
                    initial="hidden" animate="visible" variants={fadeInUp}
                    className="relative z-10 text-center px-margin-mobile flex flex-col items-center gap-6 max-w-3xl mx-auto mt-20"
                >
                    <span className="font-label-caps text-label-caps text-sage tracking-[0.2em] uppercase">Chapitre I</span>
                    <h1 className="hero-title-anim font-display-lg text-display-lg text-off-white drop-shadow-lg leading-tight">{bride} &amp;<br />{groom}</h1>
                    <p className="font-body-lg text-body-lg text-off-white/90 drop-shadow-md max-w-lg mt-4">{wedding?.venue || "Château de Fontainebleau"}, France</p>
                    <div className="mt-6 flex flex-col items-center gap-4">
                        <span className="font-headline-md text-headline-md text-off-white drop-shadow-md">{formattedDate}</span>
                        <div className="flex gap-6 mt-4 font-label-caps text-label-caps text-off-white/90 tracking-widest">
                            <div className="flex flex-col items-center"><span className="text-2xl font-headline-md mb-1">{timeLeft.days}</span><span>Jours</span></div>
                            <div className="flex flex-col items-center"><span className="text-2xl font-headline-md mb-1">{timeLeft.hours}</span><span>Heures</span></div>
                            <div className="flex flex-col items-center"><span className="text-2xl font-headline-md mb-1">{timeLeft.minutes}</span><span>Min</span></div>
                        </div>
                        <button onClick={() => setIsRSVPOpen(true)} className="font-label-caps text-label-caps mt-8 px-8 py-3 rounded-full border border-anthracite text-anthracite hover:bg-anthracite hover:text-off-white transition-colors duration-300 backdrop-blur-sm bg-surface/80 inline-block shadow-sm">Confirmer ma présence</button>
                    </div>
                </motion.div>
            </section>

            {/* 2. History Section */}
            {showSection("story") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="histoire">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-center">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="md:col-span-5 md:col-start-2">
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Chapitre I</span>
                            <h2 className="font-headline-lg text-headline-lg mb-6 text-terracotta">Notre Histoire</h2>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mb-6 whitespace-pre-wrap">
                                {wedding?.story || "C'était un après-midi de printemps doux et ensoleillé, au détour d'un sentier parfumé par la lavande. Une rencontre inattendue, un regard échange, et la certitude soudaine que nos chemins devaient se croiser.\n\nDepuis ce jour, notre amour a grandi, nourri par nos passions communes, nos voyages et les moments simples partagés avec nos proches. Aujourd'hui, nous sommes impatients d'écrire le prochain chapitre de notre histoire, entourés de vous tous."}
                            </p>
                        </motion.div>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="md:col-span-5 md:col-start-8 mt-12 md:mt-0 relative">
                            <div className="aspect-[3/4] overflow-hidden organic-shape-1 shadow-[0_40px_80px_rgba(44,44,44,0.08)]">
                                <img alt="Couple" className="w-full h-full object-cover img-desat" src={wedding?.story_image || accentImage || NATURE_ROMANTIQUE_ASSETS.story} />
                            </div>
                            <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full border border-rose-gold/30 flex items-center justify-center bg-surface-container-low/50 backdrop-blur-md">
                                <span className="material-symbols-outlined text-terracotta" style={{ fontVariationSettings: "'FILL' 1" }}>favorite</span>
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* 3. Le Grand Jour (Programme) */}
            {showSection("programme") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop bg-surface-container-low relative" id="programme">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Chapitre II</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Le Grand Jour</h2>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mt-4">Découvrez le déroulé de notre journée de célébration.</p>
                        </motion.div>
                    </div>
                    
                    <div className="relative max-w-4xl mx-auto timeline-line py-8">
                        {(wedding?.programme?.length > 0 ? wedding.programme : [
                            { title: "Cérémonie Laïque", time: "15:00", description: "Dans les jardins majestueux du domaine." },
                            { title: "Cocktail & Vin d'Honneur", time: "17:00", description: "Rafraîchissements et musique acoustique sur la terrasse sud." },
                            { title: "Dîner & Festivités", time: "20:00", description: "Banquet sous les étoiles suivi de la soirée dansante." }
                        ]).map((item, index) => {
                            const isEven = index % 2 === 0;
                            return (
                                <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className={`relative flex flex-col md:${isEven ? 'flex-row' : 'flex-row-reverse'} items-center mb-16 z-10`}>
                                    <div className={`md:w-1/2 ${isEven ? 'md:pr-12 md:text-right pl-16 md:pl-0' : 'md:pl-12 md:text-left pl-16 md:pl-0'} w-full mb-4 md:mb-0`}>
                                        <h3 className="font-headline-md text-headline-md text-anthracite">{item.title}</h3>
                                        <p className="font-body-md text-on-surface-variant mt-2">{item.description}</p>
                                        {isEven && index === 0 && (
                                            <button type="button"  className="inline-block mt-3 text-terracotta hover:text-rose-gold font-label-caps text-label-caps underline decoration-rose-gold underline-offset-4 transition-colors" onClick={(e) => {e.preventDefault(); window.scrollTo(0,0);}}>Ajouter au calendrier</button>
                                        )}
                                    </div>
                                    <div className="absolute left-[24px] md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 rounded-full bg-terracotta border-4 border-surface-container-low shadow-md"></div>
                                    <div className={`md:w-1/2 ${isEven ? 'md:pl-12 pl-16 md:pl-0' : 'md:pr-12 md:text-right pl-16 md:pl-0'} w-full`}>
                                        <span className="font-headline-lg text-headline-lg text-sage/40">{item.time}</span>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* 4. Le Lieu */}
            {showSection("infos") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="lieu">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="order-2 md:order-1">
                            <div className="aspect-square organic-shape-2 shadow-[0_20px_60px_rgba(44,44,44,0.05)] overflow-hidden">
                                <img alt="Lieu de réception" className="w-full h-full object-cover img-desat" src={wedding?.venue_image || NATURE_ROMANTIQUE_ASSETS.venue} />
                            </div>
                        </motion.div>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="order-1 md:order-2">
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Chapitre III</span>
                            <h2 className="font-headline-lg text-headline-lg mb-6 text-terracotta">Le Lieu</h2>
                            <h3 className="font-headline-md text-headline-md text-anthracite mb-4">{wedding?.venue || "Château de Fontainebleau"}</h3>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mb-8 leading-relaxed">
                                C'est au cœur de ce domaine chargé d'histoire que nous vous accueillerons. Un lieu magique où la nature rencontre l'élégance architecturale, parfait écrin pour célébrer notre union.
                            </p>
                            <a href={wedding?.location_url || `https://maps.google.com/?q=${encodeURIComponent(wedding?.venue || "Château de Fontainebleau")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-label-caps text-label-caps text-terracotta hover:text-sage transition-colors duration-300">
                                <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: "'FILL' 0" }}>map</span>
                                Découvrir le lieu
                            </a>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* 5. Hébergements */}
            {showSection("lodgings") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop bg-surface-container-low/50 relative" id="hebergements">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Où dormir</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Hébergements</h2>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mt-4 whitespace-pre-wrap">{wedding?.accommodations || "Quelques suggestions pour prolonger la fête à nos côtés."}</p>
                        </motion.div>
                    </div>
                    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {(wedding?.lodgings?.length > 0 ? wedding.lodgings : [
                            { name: "L'Aigle Noir", capacity: "À 5 min", description: "Hôtel de charme situé à quelques pas du château, offrant des chambres élégantes.", image: "/images/theme-assets/asset_f7d185b0.png" },
                            { name: "La Demeure du Parc", capacity: "À 10 min", description: "Boutique-hôtel moderne et raffiné avec un excellent restaurant.", image: "/images/theme-assets/asset_f8b0532f.png" },
                            { name: "Chambres d'Hôtes", capacity: "À 15 min", description: "Sélection de maisons d'hôtes pittoresques dans la forêt environnante.", image: "/images/theme-assets/asset_c479acb6.png" }
                        ]).map((lodging, idx) => (
                            <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="bg-surface rounded-xl p-8 shadow-[0_10px_30px_rgba(44,44,44,0.03)] border border-rose-gold/10 hover:border-terracotta/30 transition-colors flex flex-col h-full">
                                {lodging.image && <img src={lodging.image} alt={lodging.name} className="w-full h-40 object-cover rounded-lg mb-6" />}
                                <span className="font-label-caps text-label-caps text-sage/70 mb-2 block">{lodging.capacity || "Hébergement"}</span>
                                <h3 className="font-headline-md text-headline-md text-anthracite mb-3">{lodging.name}</h3>
                                <p className="font-body-md text-on-surface-variant mb-6 flex-grow">{lodging.description}</p>
                                {lodging.booking_url && <a href={lodging.booking_url} target="_blank" rel="noreferrer" className="text-terracotta hover:text-sage font-label-caps text-label-caps underline decoration-terracotta/30 underline-offset-4 transition-all">Réserver</a>}
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* 6. FAQ */}
            {showSection("faq") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="faq">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Informations</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Questions Fréquentes</h2>
                        </motion.div>
                    </div>
                    <div className="max-w-3xl mx-auto space-y-4">
                        {(wedding?.faqs?.length > 0 ? wedding.faqs : [
                            { question: "Quel est le dress code ?", answer: "Une tenue élégante mais confortable. Privilégiez les couleurs pastel ou chaudes (terre cuite, sauge, ocre)." },
                            { question: "Les enfants sont-ils conviés ?", answer: "Bien que nous adorions les enfants, nous souhaitons que les parents puissent profiter pleinement de la soirée. Nous vous invitons donc à privilégier une soirée entre adultes." },
                            { question: "Où se garer ?", answer: "Un parking privé est à votre disposition à l'entrée du domaine." }
                        ]).map((faq, idx) => (
                            <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="border border-rose-gold/20 rounded-xl overflow-hidden bg-surface">
                                <button onClick={() => toggleFaq(idx)} className="w-full text-left px-6 py-5 flex justify-between items-center focus:outline-none hover:bg-surface-container-low/30 transition-colors">
                                    <span className="font-headline-md text-anthracite">{faq.question}</span>
                                    <span className={`material-symbols-outlined text-terracotta transition-transform duration-300 ${openFaqIndex === idx ? 'rotate-180' : ''}`}>expand_more</span>
                                </button>
                                <AnimatePresence>
                                    {openFaqIndex === idx && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-6 pb-5 text-on-surface-variant font-body-md overflow-hidden">
                                            {faq.answer}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* 7. Livre d'or */}
            {showSection("guestbook") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop bg-surface-container-low/50 relative" id="livredor">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Mots doux</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Livre d'or</h2>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mt-4">Laissez-nous un petit mot pour marquer cette journée.</p>
                        </motion.div>
                    </div>
                    <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="lg:col-span-2">
                            <form onSubmit={handleMessageSubmit} className="bg-surface p-8 rounded-2xl shadow-lg border border-rose-gold/20">
                                <div className="mb-6">
                                    <label className="block font-label-caps text-label-caps text-anthracite mb-2">Votre nom</label>
                                    <input type="text" value={newMessage.guest_name} onChange={e => setNewMessage({...newMessage, guest_name: e.target.value})} className="w-full bg-surface-container-low border border-rose-gold/30 rounded-lg px-4 py-3 font-body-md text-anthracite focus:outline-none focus:border-terracotta transition-colors" placeholder="Ex: Famille Dupont" />
                                </div>
                                <div className="mb-8">
                                    <label className="block font-label-caps text-label-caps text-anthracite mb-2">Votre message</label>
                                    <textarea value={newMessage.content} onChange={e => setNewMessage({...newMessage, content: e.target.value})} rows="4" className="w-full bg-surface-container-low border border-rose-gold/30 rounded-lg px-4 py-3 font-body-md text-anthracite focus:outline-none focus:border-terracotta transition-colors resize-none" placeholder="Partagez vos voeux ou une anecdote..."></textarea>
                                </div>
                                <button type="submit" disabled={isSubmitting} className="w-full font-label-caps text-label-caps bg-terracotta text-off-white py-4 rounded-full hover:bg-sage transition-colors duration-300 disabled:opacity-50">
                                    {isSubmitting ? "Envoi..." : "Publier mon message"}
                                </button>
                            </form>
                        </motion.div>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="lg:col-span-3 space-y-6 max-h-[600px] overflow-y-auto pr-4" data-lenis-prevent="true">
                            {messages.length > 0 ? messages.map((msg) => (
                                <div key={msg.id} className="bg-surface p-6 rounded-xl shadow-sm border border-rose-gold/10">
                                    <p className="font-body-md text-anthracite italic mb-4">"{msg.content}"</p>
                                    <p className="font-label-caps text-label-caps text-sage text-right">— {msg.guest_name}</p>
                                </div>
                            )) : (
                                <div className="text-center py-12 text-on-surface-variant font-body-md italic border-2 border-dashed border-rose-gold/20 rounded-xl">
                                    Soyez les premiers à laisser un mot !
                                </div>
                            )}
                        </motion.div>
                    </div>
                </section>
            )}

            {/* 8. Faire-part */}
            {showSection("invitation") && wedding?.invitation && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="invitation">
                    <div className="max-w-4xl mx-auto">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-12">
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Découverte</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Le Faire-part</h2>
                        </motion.div>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(44,44,44,0.1)] border border-rose-gold/20">
                            <img alt="Faire-part de mariage" src={wedding.invitation} className="w-full h-auto object-cover" />
                        </motion.div>
                    </div>
                </section>
            )}
            
            {/* Dress Code & Charte de couleurs */}
            {showSection("dress_code") && (wedding?.dress_code || (wedding?.dress_code_colors && wedding.dress_code_colors.length > 0)) && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop bg-surface" id="dress-code">
                    <div className="max-w-container-max mx-auto">
                        <div className="text-center mb-16 fade-in-up visible">
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Esthétique</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Dress Code</h2>
                            {wedding.dress_code && (
                                <p className="font-body-lg text-body-lg text-on-surface-variant mt-4 max-w-2xl mx-auto">{wedding.dress_code}</p>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-start">
                            {/* Palette Palette */}
                            {wedding.dress_code_colors && wedding.dress_code_colors.length > 0 && (
                                <div className={`${!wedding.dress_code_image ? 'md:col-span-12 max-w-3xl mx-auto w-full' : 'md:col-span-4'} flex flex-col gap-6 fade-in-up visible`}>
                                    <div className="bg-surface-container-low p-8 rounded-xl editorial-shadow border border-outline-variant/10">
                                        <h3 className="font-label-caps text-label-caps text-terracotta mb-8 uppercase tracking-widest text-center">Charte Chromatique</h3>
                                        <div className="flex flex-col gap-6">
                                            {wedding.dress_code_colors.map((color, idx) => (
                                                <div key={idx} className="flex items-center gap-4 group cursor-pointer p-2 rounded-lg hover:bg-white transition-all duration-300 relative overflow-visible swatch-item" style={{transitionDelay: `${(idx + 1) * 100}ms`}} onClick={() => handleCopy(color)}>
                                                    <div className="w-14 h-14 rounded-full swatch-glow transition-transform duration-500 group-hover:scale-110 z-10 shadow-sm" style={{backgroundColor: color}}></div>
                                                    <div className="flex flex-col">
                                                        <span className="font-headline-md text-lg text-anthracite">Couleur {idx + 1}</span>
                                                        <div className="relative overflow-hidden h-4">
                                                            <span className={`font-label-caps text-[10px] text-on-surface-variant/60 transition-transform duration-300 block ${copiedColor === color ? '-translate-y-full' : 'group-hover:-translate-y-full'}`}>{color.toUpperCase()}</span>
                                                            <span className={`font-label-caps text-[10px] ${copiedColor === color ? 'text-sage translate-y-0' : 'text-terracotta group-hover:translate-y-0 translate-y-full'} font-bold absolute top-0 left-0 transition-transform duration-300 uppercase tracking-tighter`}>
                                                                {copiedColor === color ? 'COPIÉ !' : 'COPIER'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* A photo is optional: the dress code stays useful with text and colours alone. */}
                            {wedding.dress_code_image && <div className={`${(!wedding.dress_code_colors || wedding.dress_code_colors.length === 0) ? 'md:col-span-12 max-w-4xl mx-auto w-full' : 'md:col-span-8'} fade-in-up visible`} style={{transitionDelay: '200ms'}}>
                                <div className="bg-white rounded-xl overflow-hidden editorial-shadow border border-outline-variant/10 group">
                                    <div className="p-6 flex justify-between items-center border-b border-surface-container">
                                        <h3 className="font-label-caps text-label-caps text-sage uppercase tracking-widest">Inspirations Visuelles</h3>

                                    </div>
                                    <div className="p-8">
                                        <div 
                                            className="relative overflow-hidden rounded-lg parallax-container cursor-crosshair"
                                            onMouseMove={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const x = e.clientX - rect.left;
                                                const y = e.clientY - rect.top;
                                                const centerX = rect.width / 2;
                                                const centerY = rect.height / 2;
                                                const moveX = (x - centerX) / 30;
                                                const moveY = (y - centerY) / 30;
                                                const img = e.currentTarget.querySelector('img');
                                                if (img) img.style.transform = `scale(1.05) translate(${moveX}px, ${moveY}px)`;
                                            }}
                                            onMouseLeave={(e) => {
                                                const img = e.currentTarget.querySelector('img');
                                                if (img) img.style.transform = 'scale(1) translate(0, 0)';
                                            }}
                                        >
                                            <img alt="Wedding Guest Dress Code Moodboard" className="w-full h-auto object-cover rounded-sm parallax-image transition-transform duration-500" src={wedding.dress_code_image} />
                                            <div className="absolute inset-0 bg-terracotta/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-lg"></div>
                                        </div>
                                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div>
                                                <h4 className="font-headline-md text-xl mb-3 text-anthracite">Pour Elles</h4>
                                                <p className="font-body-md text-on-surface-variant">Robes longues ou midi, matières fluides, soies et satins dans des tons terreux ou pastels doux.</p>
                                            </div>
                                            <div>
                                                <h4 className="font-headline-md text-xl mb-3 text-anthracite">Pour Eux</h4>
                                                <p className="font-body-md text-on-surface-variant">Costumes élégants en lin ou coton léger, cravate optionnelle, privilégiant une palette naturelle.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        </div>
                    </div>
                </section>
            )}

            {/* 6. Souvenirs / Galerie */}
            {showSection("gallery") && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto overflow-hidden" id="galerie">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Chapitre V</span>
                            <h2 className="font-headline-lg text-headline-lg mb-6 text-terracotta">Souvenirs</h2>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mb-8">Nous avons hâte de partager cette journée avec vous. N'hésitez pas à capturer vos propres moments et à les partager dans notre galerie commune.</p>
                            
                            <button className="shimmer-btn font-label-caps text-label-caps bg-rose-gold text-off-white px-8 py-3 rounded-sm hover:bg-terracotta transition-colors duration-300 shadow-sm flex items-center gap-3">
                                <span className="material-symbols-outlined">photo_camera</span>
                                Partager vos photos
                            </button>
                        </motion.div>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="grid grid-cols-2 gap-4">
                            <div className="space-y-4 pt-12">
                                <img alt="Souvenir de mariage 1" className="w-full rounded-tr-3xl rounded-bl-3xl object-cover aspect-[3/4] shadow-md img-desat hover:scale-105 transition-transform duration-500" src={natureGalleryImages[0]} />
                                <img alt="Souvenir de mariage 2" className="w-full rounded-tl-3xl rounded-br-3xl object-cover aspect-square shadow-md img-desat hover:scale-105 transition-transform duration-500" src={natureGalleryImages[1]} />
                            </div>
                            <div className="space-y-4">
                                <img alt="Souvenir de mariage 3" className="w-full rounded-tl-3xl rounded-br-3xl object-cover aspect-square shadow-md img-desat hover:scale-105 transition-transform duration-500" src={natureGalleryImages[2]} />
                                <img alt="Souvenir de mariage 4" className="w-full rounded-tr-3xl rounded-bl-3xl object-cover aspect-[3/4] shadow-md img-desat hover:scale-105 transition-transform duration-500" src={natureGalleryImages[3]} />
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* 10. Cadeaux */}
            {showSection("gifts") && wedding?.show_gifts !== false && gifts.length > 0 && (
                <section className="py-section-gap px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="cadeaux">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                            <span className="font-label-caps text-label-caps text-sage mb-4 block tracking-[0.2em]">Avec tout notre cœur</span>
                            <h2 className="font-headline-lg text-headline-lg text-terracotta">Liste de cadeaux</h2>
                            <p className="font-body-lg text-body-lg text-on-surface-variant mt-4 max-w-2xl mx-auto">Votre présence est le plus beau des cadeaux. Si toutefois vous souhaitez nous gâter, voici quelques idées qui nous feraient plaisir.</p>
                        </motion.div>
                    </div>
                    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {gifts.map((g, idx) => (
                            <motion.div key={g.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="bg-surface border border-rose-gold/20 rounded-2xl overflow-hidden shadow-sm flex flex-col">
                                {g.image ? (
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={g.image} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                    </div>
                                ) : (
                                    <div className="aspect-[4/3] flex items-center justify-center bg-surface-container-low border-b border-rose-gold/20 text-terracotta">
                                        <div className="text-4xl font-headline-lg">{g.type === "cash" ? "€" : "🎁"}</div>
                                    </div>
                                )}
                                <div className="p-6 flex-1 flex flex-col">
                                    <span className="font-label-caps text-label-caps text-sage mb-2 block">{g.type === "cash" ? "Cagnotte" : "Cadeau"}</span>
                                    <h3 className="font-headline-md text-headline-md text-anthracite mb-3">{g.name}</h3>
                                    {g.description && <p className="font-body-md text-on-surface-variant mb-4 flex-1">{g.description}</p>}
                                    
                                    {g.type === "cash" ? (
                                        <div className="mb-4 mt-auto">
                                            {g.show_amounts !== false && <div className="flex justify-between text-xs font-label-caps text-sage mb-2">
                                                <span>{g.collected_amount || 0}€ collectés</span>
                                                <span>Objectif {g.target_amount || 0}€</span>
                                            </div>}
                                            <div className="h-1.5 rounded-full overflow-hidden bg-rose-gold/10">
                                                <div className="h-full rounded-full bg-terracotta" style={{ width: `${g.progress_percent ?? Math.min(100, ((g.collected_amount || 0) / Math.max(1, g.target_amount || 1)) * 100)}%` }} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="font-headline-md text-terracotta mb-4 mt-auto">{g.price ? `${g.price}€` : ""}</div>
                                    )}

                                    {g.claimed_by ? (
                                        <div className="font-label-caps text-label-caps text-sage text-center py-3 bg-surface-container-low rounded-xl">✓ Réservé par {g.claimed_by}</div>
                                    ) : (
                                        <button onClick={() => setClaimingGift(g)} className="w-full font-label-caps text-label-caps bg-terracotta text-off-white py-3 rounded-xl hover:bg-sage transition-colors duration-300">
                                            {g.type === "cash" ? "Contribuer" : "Réserver"}
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* Closing & Footer */}
            <section className="relative h-[60vh] flex items-center justify-center text-center overflow-hidden">
                <div className="absolute inset-0">
                    <img alt="Closing background" className="w-full h-full object-cover ken-burns" src={heroImage || NATURE_ROMANTIQUE_ASSETS.hero} />
                    <div className="absolute inset-0 bg-anthracite/40"></div>
                </div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="relative z-10 text-off-white px-margin-mobile">
                    <span className="font-label-caps text-label-caps text-off-white/80 tracking-[0.4em] mb-4 block uppercase">Et maintenant</span>
                    <h2 className="font-display-lg text-display-lg text-off-white mb-6">On vous attend.</h2>
                    <p className="font-body-md text-off-white/90 max-w-md mx-auto">Pour répondre au RSVP, utilisez le lien unique reçu par e-mail ou SMS.</p>
                    {wedding?.hashtag && <div className="mt-12 font-headline-md text-off-white/90 tracking-wider">#{wedding.hashtag}</div>}
                </motion.div>
            </section>

            <footer className="py-8 text-center bg-surface border-t border-rose-gold/20">
                {wedding?.show_social !== false && (wedding?.instagram || wedding?.pinterest) && (
                    <div className="flex justify-center gap-6 mb-6">
                        {wedding?.instagram && (
                            <a href={wedding.instagram} target="_blank" rel="noreferrer" className="text-sage hover:text-terracotta transition-colors">
                                <span className="font-label-caps tracking-widest text-xs">Instagram</span>
                            </a>
                        )}
                        {wedding?.pinterest && (
                            <a href={wedding.pinterest} target="_blank" rel="noreferrer" className="text-sage hover:text-terracotta transition-colors">
                                <span className="font-label-caps tracking-widest text-xs">Pinterest</span>
                            </a>
                        )}
                    </div>
                )}
                <p className="font-label-caps text-label-caps text-sage">
                    Site créé avec <span className="text-terracotta font-headline-sm ml-1">Ceremind</span>
                </p>
            </footer>

            <AnimatePresence>
                {claimingGift && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-anthracite/60 backdrop-blur-sm">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-surface rounded-2xl p-8 max-w-md w-full shadow-2xl">
                            <h3 className="font-headline-md text-terracotta mb-2">{claimingGift.type === "cash" ? `Contribuer à ${claimingGift.name}` : `Réserver ${claimingGift.name}`}</h3>
                            <p className="font-body-md text-on-surface-variant mb-6">{claimingGift.type === "cash" ? "Indiquez votre nom et votre participation ; vous serez ensuite redirigé(e) vers la cagnotte sécurisée." : "Veuillez indiquer votre nom pour confirmer cette réservation."}</p>
                            <form onSubmit={handleClaimGift}>
                                <input
                                    type="text"
                                    required
                                    placeholder="Votre nom"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                    className="w-full bg-surface-container-low border border-rose-gold/30 rounded-lg px-4 py-3 font-body-md text-anthracite focus:outline-none focus:border-terracotta mb-6"
                                />
                                {claimingGift.type === "cash" && (
                                    <input
                                        type="number"
                                        min="1"
                                        step="0.01"
                                        required
                                        placeholder="Votre participation (€)"
                                        value={contributionAmount}
                                        onChange={(e) => setContributionAmount(e.target.value)}
                                        className="w-full bg-surface-container-low border border-rose-gold/30 rounded-lg px-4 py-3 font-body-md text-anthracite focus:outline-none focus:border-terracotta mb-6"
                                    />
                                )}
                                <div className="flex gap-4">
                                    <button type="button" onClick={() => { setClaimingGift(null); setGuestName(""); setContributionAmount(""); }} className="flex-1 font-label-caps text-label-caps border border-sage/50 text-sage py-3 rounded-xl hover:bg-surface-container-low transition-colors">
                                        Annuler
                                    </button>
                                    <button type="submit" disabled={isClaiming || !guestName} className="flex-1 font-label-caps text-label-caps bg-terracotta text-off-white py-3 rounded-xl hover:bg-sage transition-colors disabled:opacity-50">
                                        {isClaiming ? "..." : claimingGift.type === "cash" ? "Continuer" : "Confirmer"}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <TemplateAlexandreSophieRSVP isOpen={isRSVPOpen} onClose={() => setIsRSVPOpen(false)} wedding={wedding} />
        </div>
    );
}
